import { useState } from 'react';
import { cn } from '@/lib/utils';
import { EtDateInput } from '@/components/ui/EtDateInput';
import {
  formatEtDate,
  formatEur,
  parseEurCents,
  type MarkPaidIntent,
} from '@/lib/cashFlow';

/**
 * The ONE manual mark-paid intent collector (module 1.3, L14): the SAME two-step
 * surface from the register's "Halda" popover AND the entry-detail "Arveldus"
 * section. Step 1 collects the settlement date + the scope (the FULL outstanding
 * gap or a PARTIAL euro amount); step 2 is the explicit confirmation ("kas ikka
 * soovid?") with the source-priority note — a manual payment is a human
 * observation that a later real sync (pank / raamatupidamine / müügisüsteem)
 * supersedes. The panel never decides money (L9): a partial is only an INTENT the
 * server validates against the authoritative gap; "full" sends no amount at all.
 * The parent mints the idempotency key and closes the panel on dispatch.
 */
export interface MarkPaidPanelProps {
  /** The entry's outstanding gap (integer cents, magnitude) — display + input gate only. */
  outstandingCents: number;
  /** Direction wording: income → "laekunuks", cost → "tasutuks". */
  isIncome: boolean;
  /** Initial settlement date (ISO) — the register passes its action date, the detail today. */
  defaultDate: string;
  /** The mutation is in flight — the confirm button locks. */
  pending: boolean;
  /** Dense register-popover sizing (text-[10px]); default is the detail's 12px scale. */
  compact?: boolean;
  onConfirm: (intent: MarkPaidIntent) => void;
  onCancel: () => void;
}

/** The two amount scopes step 1 offers. */
const SCOPES = ['full', 'partial'] as const;
type Scope = (typeof SCOPES)[number];

export function MarkPaidPanel({
  outstandingCents,
  isIncome,
  defaultDate,
  pending,
  compact = false,
  onConfirm,
  onCancel,
}: MarkPaidPanelProps) {
  const [paidOn, setPaidOn] = useState(defaultDate);
  const [scope, setScope] = useState<Scope>('full');
  const [amountText, setAmountText] = useState('');
  const [confirming, setConfirming] = useState(false);

  const verb = isIncome ? 'laekunuks' : 'tasutuks';
  const scopeLabel: Record<Scope, string> = {
    full: 'Kogu jääk',
    partial: 'Osaline summa',
  };

  // The partial gate mirrors the split panel: a clean positive euro amount within the
  // outstanding gap (comparison only, never arithmetic — the server re-validates, L9).
  const partialCents = parseEurCents(amountText);
  const partialOk = partialCents !== null && partialCents > 0 && partialCents <= outstandingCents;
  const formOk = scope === 'full' || partialOk;
  // What the confirmation names: the full gap, or the accepted partial (0 is never
  // shown — the button to reach the confirm step is gated on formOk).
  const confirmCents = scope === 'full' ? outstandingCents : (partialCents ?? 0);
  const intent: MarkPaidIntent =
    scope === 'full' ? { paidOn } : { paidOn, amountCents: partialCents ?? 0 };

  const base = compact ? 'text-[10px]' : 'text-[12px]';
  const btn = compact ? 'px-1.5 py-0.5 text-[10px]' : 'px-2 py-1 text-[12px]';

  return (
    <div
      data-testid="mark-paid-panel"
      className={cn('flex flex-col items-stretch gap-1.5 rounded border border-primary/40 bg-primary/5 p-2', base)}
    >
      {confirming ? (
        <>
          <p className="text-foreground">
            Märgid {verb}{' '}
            <span className="tabular-nums font-semibold">{formatEur(confirmCents)}</span>
            {scope === 'partial' && (
              <span className="text-muted-foreground"> (osaline, jääk {formatEur(outstandingCents)})</span>
            )}{' '}
            kuupäevaga <span className="tabular-nums">{formatEtDate(paidOn)}</span>. Kas soovid
            jätkata?
          </p>
          <p className="text-muted-foreground">
            Käsitsi makse on inimese tähelepanek — kui pank, raamatupidamine või müügisüsteem
            hiljem sama arvelduse sünkroonib, prioritiseerib see allikas käsitsi kirje üle.
          </p>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={pending}
              onClick={() => onConfirm(intent)}
              className={cn(
                'rounded border border-[hsl(var(--income))]/50 font-medium text-[hsl(var(--income))] transition-colors hover:bg-[hsl(var(--income))]/10 disabled:cursor-not-allowed disabled:opacity-40',
                btn,
              )}
            >
              Kinnita
            </button>
            <button
              type="button"
              onClick={() => setConfirming(false)}
              className={cn('rounded border border-border text-muted-foreground transition-colors hover:text-foreground', btn)}
            >
              Tagasi
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="flex items-center gap-1.5 text-muted-foreground">
            <span>Kuupäev</span>
            <EtDateInput
              value={paidOn}
              onChangeIso={setPaidOn}
              aria-label="Makse kuupäev"
              title="Vali makse kuupäev"
              className={compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-1 text-[12px]'}
            />
          </label>
          <div className="flex flex-wrap gap-1">
            {SCOPES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setScope(s)}
                aria-pressed={scope === s}
                className={cn(
                  'rounded border font-medium transition-colors',
                  btn,
                  scope === s
                    ? 'border-primary/60 bg-primary/15 text-primary'
                    : 'border-border text-muted-foreground hover:text-foreground',
                )}
              >
                {scopeLabel[s]}
              </button>
            ))}
          </div>
          {scope === 'partial' ? (
            <label className="flex items-center gap-1 text-muted-foreground">
              <input
                type="text"
                inputMode="decimal"
                aria-label="Makse summa eurodes"
                value={amountText}
                onChange={(ev) => setAmountText(ev.target.value)}
                placeholder="nt 500"
                className={cn(
                  'w-24 rounded border border-border bg-background text-foreground outline-none focus:border-primary/60',
                  compact ? 'px-1 py-0.5 text-[10px]' : 'px-1.5 py-1 text-[12px]',
                )}
              />
              <span>€ · jääk {formatEur(outstandingCents)}</span>
            </label>
          ) : (
            <span className="text-muted-foreground">
              Kogu jääk <span className="tabular-nums text-foreground/80">{formatEur(outstandingCents)}</span>
            </span>
          )}
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              disabled={!formOk}
              onClick={() => setConfirming(true)}
              className={cn(
                'rounded border border-primary/50 bg-primary/15 font-medium text-primary transition-colors hover:bg-primary/25 disabled:cursor-not-allowed disabled:opacity-40',
                btn,
              )}
            >
              Edasi
            </button>
            <button
              type="button"
              onClick={onCancel}
              className={cn('rounded border border-border text-muted-foreground transition-colors hover:text-foreground', btn)}
            >
              Tühista
            </button>
          </div>
        </>
      )}
    </div>
  );
}
